import {
  LibraryEntity,
  LibraryList,
  ShareLink,
  ShareList,
  type CreateShareInput,
  type EpisodeRef,
  type LibraryKind,
} from "@dramapie/shared";
import { z } from "zod";

/**
 * M5（资料库 / 分享）的纯展示逻辑。
 *
 * 真源是 `packages/shared`（`LibraryEntity.refUrls`、`ShareLink.id` 都已冻结）。
 * 这里只放两类东西：
 * - 展示格式化（掩码、过期、409 引用文案）；
 * - 对**老形状**的窄兜底（例如创建响应缺 `scope` 时用请求入参补齐），
 *   绝不因为响应少字段就把一次成功的操作显示成失败。
 */

// ---------------------------------------------------------------- 资料库：列表

/**
 * 资料库实体 = shared 的 `LibraryEntity`（含 `refUrls` 现签 URL）。
 * 保留别名只是为了让组件层不直接依赖 shared 的命名。
 */
export type LibraryEntityView = LibraryEntity;

/** 兼容 `{ items }` / 裸数组两种列表形状。 */
export function parseLibraryList(payload: unknown): LibraryEntityView[] {
  const wrapped = LibraryList.safeParse(payload);
  if (wrapped.success) return wrapped.data.items;
  const bare = z.array(LibraryEntity).safeParse(payload);
  return bare.success ? bare.data : [];
}

/**
 * 新建 / 修改的响应：契约没写死是裸实体还是包一层。
 * 三种都认，认不出来交给调用方回退到「重新拉列表」。
 */
export function libraryEntityFromPayload(payload: unknown): LibraryEntityView | null {
  const direct = LibraryEntity.safeParse(payload);
  if (direct.success) return direct.data;
  if (isRecord(payload)) {
    for (const key of ["item", "entity", "library"]) {
      const wrapped = LibraryEntity.safeParse(payload[key]);
      if (wrapped.success) return wrapped.data;
    }
  }
  return null;
}

// ---------------------------------------------------------------- 资料库：展示

export interface LibraryTabMeta {
  label: string;
  singular: string;
  /** 「新建角色」 */
  createLabel: string;
  emptyTitle: string;
  emptyHint: string;
}

export const LIBRARY_TABS: Record<LibraryKind, LibraryTabMeta> = {
  character: {
    label: "角色",
    singular: "角色",
    createLabel: "新建角色",
    emptyTitle: "还没有角色",
    emptyHint: "角色会被镜头引用；建好之后镜头里就能选到（契约 M5 §1）。",
  },
  location: {
    label: "场景",
    singular: "场景",
    createLabel: "新建场景",
    emptyTitle: "还没有场景",
    emptyHint: "场景记录时间段与参考图，镜头按 sceneId 关联场次。",
  },
  prop: {
    label: "道具",
    singular: "道具",
    createLabel: "新建道具",
    emptyTitle: "还没有道具",
    emptyHint: "道具只存名字与描述，可附参考图。",
  },
};

export const LIBRARY_KINDS: readonly LibraryKind[] = ["character", "location", "prop"];

/** 角色的定位（主角/配角/龙套）；场景与道具没有这个概念。 */
export function entityRoleLabel(entity: LibraryEntityView): string | null {
  const role = entity.role?.trim();
  return role ? role : null;
}

/**
 * 卡片正文：角色/道具用 `look`（中文外形描述），场景用 `notes` 兜底。
 * 空字符串一律当成「没有」，避免渲染一行空白把卡片撑高。
 */
export function entityDescription(entity: LibraryEntityView): string | null {
  const candidates =
    entity.kind === "location"
      ? [entity.look, entity.notes]
      : [entity.look, entity.kind === "prop" ? entity.notes : null];
  for (const candidate of candidates) {
    const value = candidate?.trim();
    if (value) return value;
  }
  return null;
}

/** 场景的时间标签（清晨 / 日 / 夜…），已去重去空。 */
export function entityTimeTags(entity: LibraryEntityView): string[] {
  return [...new Set(entity.time.map((tag) => tag.trim()).filter(Boolean))];
}

function isHttpUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  return /^https?:\/\//i.test(value);
}

/**
 * 参考图的**签名 URL**（`refUrls[0]`，与 `refs` 一一对应）。
 *
 * `refs` 是 R2 key，不能拼地址：拿不到现签 URL 就返回 null，调用方画首字占位。
 */
export function libraryRefUrl(entity: LibraryEntityView): string | null {
  for (const candidate of entity.refUrls ?? []) {
    if (isHttpUrl(candidate)) return candidate;
  }
  return null;
}

/** 已上传的参考图数量（key 与 URL 都算，前端不重复下载）。 */
export function libraryRefCount(entity: LibraryEntityView): number {
  return entity.refs.length;
}

/** 占位块里的「首字」：按码点取，中文/emoji 都不会被截半个。 */
export function initialOf(name: string): string {
  const chars = [...name.trim()];
  return chars[0] ?? "·";
}

/** 卡片副标题（角色 = 定位；场景 = 时间段；道具 = null）。 */
export function entitySubtitle(entity: LibraryEntityView): string | null {
  if (entity.kind === "character") return entityRoleLabel(entity);
  if (entity.kind === "location") {
    const tags = entityTimeTags(entity);
    return tags.length > 0 ? tags.join(" · ") : null;
  }
  return null;
}

// ---------------------------------------------------------------- 资料库：409 引用

export interface ReferenceRef {
  episodeId: string;
  shotId: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 从 409 里抠出 `details.referencedBy`（契约 M5 §1）。
 * 兼容三种放置位置：`details` 参数、`error.details`、以及引用清单直接是对象。
 */
export function referencedByFromDetails(
  details?: Record<string, unknown> | null,
  payload?: unknown,
): ReferenceRef[] {
  const candidates: unknown[] = [];
  if (details) candidates.push(details.referencedBy, details);
  if (isRecord(payload)) {
    const errorField = payload.error;
    if (isRecord(errorField) && isRecord(errorField.details)) {
      candidates.push(errorField.details.referencedBy, errorField.details);
    }
    candidates.push(payload.referencedBy);
  }

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const parsed = candidate
      .map((item): ReferenceRef | null => {
        if (!isRecord(item)) return null;
        const episodeId = typeof item.episodeId === "string" ? item.episodeId : "";
        const shotId = typeof item.shotId === "string" ? item.shotId : "";
        if (!episodeId && !shotId) return null;
        return { episodeId, shotId };
      })
      .filter((item): item is ReferenceRef => item !== null);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

/** `ep01` → `第1集`（用项目里的集列表，查不到就原样显示 id）。 */
export function episodeLabel(episodes: readonly EpisodeRef[], episodeId: string): string {
  if (!episodeId) return "未知集";
  const episode = episodes.find((item) => item.id === episodeId);
  return episode ? `第${episode.no}集` : episodeId;
}

/**
 * 「被 第1集 · sht_s2 等 3 处引用」。
 * 只有一处时不写「等 N 处」，别把 1 说成一批。
 */
export function referencedByText(
  refs: readonly ReferenceRef[],
  episodes: readonly EpisodeRef[] = [],
): string {
  if (refs.length === 0) return "仍被镜头引用，不能删除";
  const first = refs[0]!;
  const head = [episodeLabel(episodes, first.episodeId), first.shotId]
    .filter(Boolean)
    .join(" · ");
  return refs.length === 1 ? `被 ${head} 引用` : `被 ${head} 等 ${refs.length} 处引用`;
}

// ---------------------------------------------------------------- 分享

/**
 * 创建响应：线上后端返回完整 `ShareLink`（含 `id`）。
 * `scope / episodeId / allowDownload` 若缺失（老形状）用请求入参补齐；
 * 但 `id` 必须有——没有 id 就无法撤销，宁可当成失败让用户重试。
 */
const SharePartial = z.object({
  id: ShareLink.shape.id,
  token: z.string().min(1),
  url: z.string().min(1),
  scope: ShareLink.shape.scope.optional(),
  episodeId: ShareLink.shape.episodeId.optional(),
  allowDownload: z.boolean().optional(),
  expiresAt: z.number(),
  createdAt: z.number().optional(),
});

/**
 * 单个分享链接：优先按 shared 的 `ShareLink` 解析，失败再走兜底补齐。
 */
export function shareLinkFromPayload(payload: unknown, input: CreateShareInput): ShareLink | null {
  const strict = ShareLink.safeParse(payload);
  if (strict.success) return strict.data;
  const parsed = SharePartial.safeParse(payload);
  if (!parsed.success) return null;
  const data = parsed.data;
  return {
    id: data.id,
    token: data.token,
    url: data.url,
    scope: data.scope ?? input.scope,
    episodeId: data.episodeId ?? input.episodeId ?? null,
    allowDownload: data.allowDownload ?? input.allowDownload ?? false,
    expiresAt: data.expiresAt,
    createdAt: data.createdAt ?? Date.now(),
  };
}

/** 列表项兜底：缺 `scope / episodeId / allowDownload / createdAt` 时补安全默认值。 */
function normalizeShare(data: z.infer<typeof SharePartial>, createdAt: number): ShareLink {
  return {
    id: data.id,
    token: data.token,
    url: data.url,
    scope: data.scope ?? "storyboard",
    episodeId: data.episodeId ?? null,
    allowDownload: data.allowDownload ?? false,
    expiresAt: data.expiresAt,
    createdAt: data.createdAt ?? createdAt,
  };
}

/** 分享列表：兼容 `{ items }` / 裸数组 / 缺字段的老形状。线上列表 token/url 已是掩码。 */
export function parseShareList(payload: unknown): ShareLink[] {
  const wrapped = ShareList.safeParse(payload);
  if (wrapped.success) return wrapped.data.items;

  const partial = z.object({ items: z.array(SharePartial) }).safeParse(payload);
  if (partial.success) return partial.data.items.map((item) => normalizeShare(item, 0));

  const bare = z.array(ShareLink).safeParse(payload);
  return bare.success ? bare.data : [];
}

/** 服务端已经掩码过的 token：`shr_5qYl…`。再掩一次会把掩码本身截坏。 */
export function isMaskedToken(token: string): boolean {
  return token.includes("…") || token.includes("...");
}

/**
 * `shr_abcdefghijklmnop` → `shr_abcd…mnop`。
 *
 * 幂等：列表里的 token 已经是掩码（`shr_5qYl…`）时原样返回。
 */
export function maskToken(token: string): string {
  if (!token) return "…";
  if (isMaskedToken(token)) return token;
  if (token.length <= 4) return "…";
  if (token.length <= 10) return `${token.slice(0, 3)}…`;
  const head = token.slice(0, Math.min(8, token.length - 4));
  return `${head}…${token.slice(-4)}`;
}

export function shareScopeLabel(
  scope: CreateShareInput["scope"],
  episode?: EpisodeRef | null,
): string {
  if (scope === "episode") {
    return episode ? `当前集 · 第${episode.no}集 ${episode.title}` : "当前集";
  }
  return "整集故事板";
}

/** `url` 可能是相对路径（开发代理 / 同域部署），展示时补全成完整 URL。 */
export function absoluteShareUrl(url: string, origin?: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const base =
    origin ?? (typeof window !== "undefined" ? window.location.origin : "https://dramapie.com");
  const suffix = url.startsWith("/") ? url : `/${url}`;
  return `${base}${suffix}`;
}

/**
 * 过期说明：`已过期` / `剩 7 天` / `不足 1 天`。
 *
 * 向上取整：7 天有效的链接刚生成时剩余 6.999 天，显示「剩 7 天」才符合用户预期；
 * 不足一天单独说，避免出现「剩 0 天」。
 */
export function shareExpiryNote(expiresAt: number, now: number = Date.now()): string {
  if (!Number.isFinite(expiresAt)) return "有效期未知";
  const diff = expiresAt - now;
  if (diff <= 0) return "已过期";
  if (diff < 86_400_000) return "不足 1 天";
  return `剩 ${Math.ceil(diff / 86_400_000)} 天`;
}

export function isShareExpired(expiresAt: number, now: number = Date.now()): boolean {
  return Number.isFinite(expiresAt) && expiresAt <= now;
}

export const SHARE_SCOPE_OPTIONS: readonly {
  scope: CreateShareInput["scope"];
  label: string;
  hint: string;
}[] = [
  {
    scope: "storyboard",
    label: "整集故事板",
    hint: "只读展示整集镜头与场次，不含其它集",
  },
  {
    scope: "episode",
    label: "当前集",
    hint: "把链接固定到当前这一集",
  },
];

export const SHARE_DAY_OPTIONS: readonly number[] = [1, 7, 30];

/** 复制到剪贴板：优先 Clipboard API，失败时退回 textarea + execCommand。 */
export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  const clipboard =
    typeof navigator === "undefined" ? undefined : navigator.clipboard?.writeText?.bind(navigator.clipboard);
  if (clipboard) {
    try {
      await clipboard(text);
      return true;
    } catch {
      // 权限被拒 / 非安全上下文：走下面的兜底。
    }
  }
  if (typeof document === "undefined") return false;
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.top = "-1000px";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok =
      typeof document.execCommand === "function" ? document.execCommand("copy") : false;
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
